import * as React from 'react';

import classnames from 'classnames';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {AnimateSuccess} from '../animate-success/animate-success';
import {ErrorDialog} from '../dialog/error-dialog';

import {EditInPlaceTextfield} from './edit-in-place-textfield';

import styles from './edit-in-place-cell.module.css';

type Props = {
    name: string;
    placeholder?: string;
    inactiveCell: React.ReactElement<any>;
    onChange: (newValue?: string | null | undefined) => Promise<any> | undefined;
    value: string | null | undefined;
    type?: HTMLInputElement['type'];
    // Allow hiding the hover state. This will continue to change the cursor type
    disableDefaultInteractionStates?: boolean;
};

export function EditInPlaceCell(props: Props) {
    const [isEditing, setIsEditing] = React.useState<boolean>(false);
    const [tempValue, setTempValue] = React.useState<string | null | undefined>(props.value);
    const [recentlyUpdated, setRecentlyUpdated] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null | undefined>(null);
    const cellRef = React.useRef(null);

    const handleCancel = () => {
        setTempValue(props.value);
        setIsEditing(false);
    };

    const handleKeyPress = React.useCallback(
        (event: React.KeyboardEvent<any>) => {
            if (!isEditing && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                setIsEditing(true);
            }
        },
        [isEditing]
    );

    const handleRecentlyUpdated = () => {
        setRecentlyUpdated(true);
        setTimeout(() => {
            setRecentlyUpdated(false);
        }, 3000);
    };

    return (
        <div
            onKeyPress={handleKeyPress}
            ref={cellRef}
            tabIndex={isEditing ? undefined : 0}
            className={props.disableDefaultInteractionStates ? styles.cell : styles['cell--hover']}
        >
            {isEditing ? (
                <EditInPlaceTextfield
                    name={props.name}
                    type={props.type}
                    shouldAutoFocus={true}
                    value={tempValue}
                    onChange={setTempValue}
                    onSubmit={() => {
                        // Only register a change if the value has changed
                        if (tempValue !== props.value) {
                            const onChange = props.onChange(tempValue);
                            if (onChange instanceof Promise) {
                                onChange
                                    .then(() => {
                                        handleRecentlyUpdated();
                                    })
                                    .catch((error) => {
                                        setErrorMessage(safelyGetGraphQLErrorMessage(error));
                                    });
                            } else {
                                handleRecentlyUpdated();
                            }
                        }

                        setIsEditing(false);
                    }}
                    onCancel={handleCancel}
                    blinkSuccess={false}
                    multiline={false}
                    placeholder={props.placeholder}
                />
            ) : (
                <AnimateSuccess active={recentlyUpdated}>
                    <div
                        onClick={() => setIsEditing(true)}
                        className={classnames(
                            // @ts-expect-error
                            props.disableDefaultInteractionStates ? styles['no-hover'] : undefined,
                            styles['cell-inactive']
                        )}
                    >
                        {props.inactiveCell}
                    </div>
                </AnimateSuccess>
            )}
            {errorMessage ? (
                <ErrorDialog
                    anchor={cellRef.current}
                    location='bottom'
                    message={errorMessage}
                    onClose={() => setErrorMessage(null)}
                />
            ) : null}
        </div>
    );
}
